import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Award, Target, Coins, Edit, Users as UsersIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { updateProfile, getTeam, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="tgl-card cut-corners p-4 flex items-center gap-3">
      <Icon size={18} className="text-[#D4AF37]" />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="font-display text-xl font-black text-white">{value}</div>
      </div>
    </div>
  );
}

function EditProfileDialog({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    display_name: user.display_name || "",
    gamertag: user.gamertag || "",
    bio: user.bio || "",
    avatar_url: user.avatar_url || "",
    discord: user.discord || "",
    twitch: user.twitch || "",
  });
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await updateProfile(form);
      toast.success("Profile updated.");
      onSaved(updated);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="edit-profile-btn" variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10">
          <Edit size={14} className="mr-2" /> Edit Profile
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-[#D4AF37]/30 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Edit Profile</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="grid gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Display Name</Label>
              <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                data-testid="profile-name-input"
                className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Gamertag</Label>
              <Input value={form.gamertag} onChange={(e) => setForm({ ...form, gamertag: e.target.value })}
                data-testid="profile-gamertag-input"
                className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Avatar URL</Label>
            <Input value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
              data-testid="profile-avatar-input"
              placeholder="https://..."
              className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Bio</Label>
            <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
              data-testid="profile-bio-input" rows={3}
              className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Discord</Label>
              <Input value={form.discord} onChange={(e) => setForm({ ...form, discord: e.target.value })}
                className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Twitch</Label>
              <Input value={form.twitch} onChange={(e) => setForm({ ...form, twitch: e.target.value })}
                className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]" />
            </div>
          </div>
          <Button type="submit" disabled={saving} data-testid="profile-save-btn"
            className="bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest cut-corners">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ProfilePage() {
  const { user, setUser, loading } = useAuth();
  const [team, setTeam] = useState(null);

  useEffect(() => {
    if (user?.team_id) {
      getTeam(user.team_id).then(setTeam).catch(() => setTeam(null));
    } else {
      setTeam(null);
    }
  }, [user?.team_id]);

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#050505] text-zinc-400">Loading...</div>;
  if (!user) return <Navigate to="/auth" replace state={{ from: "/profile" }} />;
  const stats = user.stats || {};
  const winRate = stats.matches_played ? Math.round((stats.matches_won / stats.matches_played) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-20 px-6">
      <Navbar />
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row items-start gap-6 mb-10">
          <div className="w-28 h-28 rounded-full overflow-hidden ring-2 ring-[#D4AF37]/40 bg-black flex items-center justify-center">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt={user.display_name} className="w-full h-full object-cover" />
            ) : (
              <span className="font-display text-4xl gold-text">
                {(user.gamertag || user.display_name || "?")[0].toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37]">⌁ Profile</p>
            <h1 className="mt-1 font-display text-3xl lg:text-4xl font-black uppercase text-white" data-testid="profile-name">
              {user.display_name}
            </h1>
            <p className="text-zinc-400">@{user.gamertag || user.email}</p>
            {user.bio && <p className="mt-2 text-sm text-zinc-300 max-w-xl">{user.bio}</p>}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {team && (
                <Link to={`/teams/${team.id}`} className="inline-flex items-center gap-2 border border-[#D4AF37]/40 px-3 py-1 cut-corners text-xs uppercase tracking-widest text-[#FFE9A0]">
                  <UsersIcon size={12} className="text-[#D4AF37]" /> {team.name} {team.captain_id === user.user_id && "· Captain"}
                </Link>
              )}
              {user.role === "admin" && (
                <span className="inline-flex items-center border border-[#D4AF37] px-3 py-1 cut-corners text-xs uppercase tracking-widest text-[#D4AF37]">Admin</span>
              )}
            </div>
          </div>
          <EditProfileDialog user={user} onSaved={setUser} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Trophy} label="Tournaments" value={stats.tournaments || 0} />
          <StatCard icon={Award} label="Wins" value={stats.wins || 0} />
          <StatCard icon={Target} label="Win Rate" value={`${winRate}%`} />
          <StatCard icon={Coins} label="Prize Earned" value={`$${stats.prize_total || 0}`} />
        </div>

        <div className="tgl-card cut-corners p-6">
          <h3 className="font-display uppercase text-lg mb-4">Recent Matches</h3>
          <p className="text-zinc-500 text-sm">No matches yet. Register your team for a tournament to start filling this log.</p>
        </div>
      </div>
    </div>
  );
}
